import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { mainSiteUrl, categories } from '@/mocks/categories';
import { articles, type ArticleContent } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';

function generateFaqSchema(article: ArticleContent) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

export default function ArticleLayout({ slug }: { slug: string }) {
  const article = articles[slug];

  useEffect(() => {
    if (!article) return;
    const faqSchema = generateFaqSchema(article);
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(faqSchema);
    document.head.appendChild(script);

    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.metaDescription,
      datePublished: '2026-07-01',
      dateModified: '2026-07-13',
      author: {
        '@type': 'Organization',
        name: 'Superbuy Spreadsheet',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Superbuy Spreadsheet',
        url: import.meta.env.VITE_SITE_URL || 'https://superbuyspread.makeup/',
      },
    };
    const articleScript = document.createElement('script');
    articleScript.type = 'application/ld+json';
    articleScript.text = JSON.stringify(articleSchema);
    document.head.appendChild(articleScript);

    return () => {
      document.head.removeChild(script);
      document.head.removeChild(articleScript);
    };
  }, [article]);

  if (!article) {
    return (
      <main>
        <div className="min-h-screen flex items-center justify-center bg-background-50 px-4">
          <div className="text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-3">Article Not Found</h1>
            <p className="text-foreground-600 mb-6" style={{ fontFamily: 'var(--font-body)' }}>The article you are looking for does not exist.</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full font-semibold text-sm hover:bg-primary-600 transition-all cursor-pointer" style={{ fontFamily: 'var(--font-label)' }}>
              Back to Home
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main>
      <Navbar />

      <article>
        <header className="relative bg-background-950 text-white pt-28 pb-20 md:pt-32 md:pb-28 px-4 md:px-10">
          <div className="max-w-4xl mx-auto">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-white/60 hover:text-white/90 text-sm transition-colors duration-200 mb-8 cursor-pointer"
              style={{ fontFamily: 'var(--font-label)' }}
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>

            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-white/80 text-xs mb-6" style={{ fontFamily: 'var(--font-label)' }}>
              <i className="ri-article-line"></i>
              {article.readTime}
            </div>

            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
              {article.title}
            </h1>
            <p className="text-white/70 text-base md:text-lg max-w-2xl leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>
              {article.subtitle}
            </p>

            <div className="flex flex-wrap items-center gap-4 mt-8 text-white/50 text-xs" style={{ fontFamily: 'var(--font-body)' }}>
              <span>Updated: July 2026</span>
              <span className="w-1 h-1 rounded-full bg-white/30"></span>
              <span>14 Categories Covered</span>
              <span className="w-1 h-1 rounded-full bg-white/30"></span>
              <span>Independent Research</span>
            </div>
          </div>
        </header>

        <section className="relative py-16 md:py-20 px-4 md:px-10 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-10">
              {article.sections.map((section, idx) => (
                <div key={idx} className="bg-white rounded-3xl p-8 md:p-10 border border-background-200">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                    {section.heading}
                  </h2>

                  {section.paragraphs.map((para, pIdx) => (
                    <p
                      key={pIdx}
                      className="text-foreground-700 leading-relaxed text-base mb-5 last:mb-0"
                      style={{ fontFamily: 'var(--font-body)' }}
                      dangerouslySetInnerHTML={{ __html: para }}
                    ></p>
                  ))}

                  {section.highlightCards && section.highlightCards.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                      {section.highlightCards.map((card, cIdx) => (
                        <div key={cIdx} className="p-5 rounded-2xl bg-accent-50 border border-accent-200">
                          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                            <i className={card.icon}></i>
                          </div>
                          <h4 className="font-semibold text-foreground-900 text-sm mb-1.5" style={{ fontFamily: 'var(--font-label)' }}>{card.title}</h4>
                          <p className="text-foreground-600 text-xs leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>{card.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              <div className="bg-white rounded-3xl p-8 md:p-10 border border-background-200">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  Frequently Asked Questions
                </h2>

                <div className="space-y-5">
                  {article.faqs.map((faq, fIdx) => (
                    <div key={fIdx}>
                      <h4 className="font-semibold text-foreground-900 text-sm mb-1.5" style={{ fontFamily: 'var(--font-label)' }}>
                        {faq.question}
                      </h4>
                      <p className="text-foreground-600 text-sm leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>
                        {faq.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-3xl p-8 md:p-10 border border-background-200">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                  Related Articles &amp; Resources
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {Object.entries(articles)
                    .filter(([s]) => s !== slug)
                    .slice(0, 4)
                    .map(([s, a]) => (
                      <Link
                        key={s}
                        to={`/${s}`}
                        className="flex items-center gap-3 p-3.5 rounded-xl border border-background-200 hover:border-primary-300 hover:bg-primary-50/20 transition-all duration-200 cursor-pointer"
                      >
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                          <i className="ri-article-line text-sm"></i>
                        </div>
                        <span className="text-sm font-medium text-foreground-700 line-clamp-1" style={{ fontFamily: 'var(--font-label)' }}>
                          {a.title}
                        </span>
                        <i className="ri-arrow-right-line text-foreground-300 text-xs ml-auto flex-shrink-0"></i>
                      </Link>
                    ))}
                </div>
              </div>

              <div className="bg-primary-50 rounded-3xl p-8 md:p-10 border border-primary-200 text-center">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  Ready to Explore Superbuy Sheet?
                </h2>
                <p className="text-foreground-600 text-base max-w-lg mx-auto mb-6" style={{ fontFamily: 'var(--font-body)' }}>
                  Browse all 14 organized categories and discover thousands of curated product finds. Start your
                  smarter shopping journey today with Superbuy Spreadsheet.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link
                    to="/"
                    className="whitespace-nowrap inline-flex items-center gap-2 px-7 py-3 bg-foreground-950 text-white rounded-full font-semibold text-sm hover:bg-foreground-800 transition-all duration-300 cursor-pointer"
                    style={{ fontFamily: 'var(--font-label)' }}
                  >
                    <i className="ri-home-4-line"></i>
                    Back to Homepage
                  </Link>
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="whitespace-nowrap inline-flex items-center gap-2 px-7 py-3 bg-primary-500 text-white rounded-full font-semibold text-sm hover:bg-primary-600 transition-all duration-300 cursor-pointer"
                    style={{ fontFamily: 'var(--font-label)' }}
                  >
                    <i className="ri-external-link-line"></i>
                    Visit Main Website
                  </a>
                </div>
              </div>

              <div className="bg-white rounded-3xl p-6 md:p-8 border border-background-200">
                <h3 className="font-semibold text-foreground-800 text-sm mb-4" style={{ fontFamily: 'var(--font-label)' }}>
                  Browse Product Categories
                </h3>
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-background-200 text-xs text-foreground-600 hover:border-primary-300 hover:text-primary-700 hover:bg-primary-50/30 transition-all duration-200 cursor-pointer"
                      style={{ fontFamily: 'var(--font-body)' }}
                    >
                      <i className={`${cat.icon} text-xs text-secondary-500`}></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <footer className="relative bg-background-950 text-white py-10 px-4 md:px-10">
          <div className="max-w-4xl mx-auto text-center">
            <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-white/80 text-sm transition-colors duration-200 mb-4 cursor-pointer" style={{ fontFamily: 'var(--font-label)' }}>
              <i className="ri-arrow-left-line"></i>
              Return to Superbuy Spreadsheet Home
            </Link>
            <p className="text-white/30 text-xs" style={{ fontFamily: 'var(--font-body)' }}>
              &copy; {new Date().getFullYear()} Superbuy Spreadsheet. Independent research platform. Not affiliated with any agent platform.
            </p>
          </div>
        </footer>
      </article>
    </main>
  );
}